import type { EntitySourceEvidence, StandReport } from "../shared/types";

export type ManagersPanelRow = {
  id: string;
  name: string;
  role: string;
  detail: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const hasText = (value: string | null | undefined): value is string => Boolean(value?.trim());

const getManagersEvidence = (report: StandReport): EntitySourceEvidence | undefined => {
  const categoryEvidence = report.categories
    .find((category) => category.sourceId === "managers")
    ?.sourceEvidence?.find((evidence) => evidence.provider === "managers_public_records");

  if (categoryEvidence) {
    return categoryEvidence;
  }

  return report.entity.sourceEvidence.find((evidence) => evidence.provider === "managers_public_records");
};

const formatQuotas = (quotasPercent: unknown, quotasEur: unknown) => {
  const parts: string[] = [];

  if (typeof quotasPercent === "number" && Number.isFinite(quotasPercent) && quotasPercent > 0) {
    parts.push(`${quotasPercent}%`);
  }

  if (typeof quotasEur === "number" && Number.isFinite(quotasEur) && quotasEur > 0) {
    parts.push(
      new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(quotasEur),
    );
  }

  return parts.join(" · ");
};

const mapManagementRows = (management: unknown[]): ManagersPanelRow[] =>
  management
    .map((item) => asRecord(item))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => {
      const name = String(item.name ?? "").trim();
      if (!hasText(name)) {
        return null;
      }

      const role = String(item.role ?? "Gerente").trim() || "Gerente";
      const body = String(item.body ?? "").trim();
      const since = String(item.since ?? "").trim();
      const detail = [body, since ? `desde ${since}` : ""].filter(hasText).join(" · ");

      return {
        id: `management-${String(item.nif ?? name)}`,
        name,
        role,
        detail: detail || "Identificado via MJ.ENGINO.PT",
      };
    })
    .filter((row): row is ManagersPanelRow => Boolean(row));

const mapSocioRows = (socios: unknown[]): ManagersPanelRow[] =>
  socios
    .map((item) => asRecord(item))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => {
      const name = String(item.name ?? "").trim();
      if (!hasText(name)) {
        return null;
      }

      const role = String(item.role ?? "Sócio").trim() || "Sócio";
      const detail = formatQuotas(item.quotas_percent, item.quotas_eur) || "Titular identificado via MJ.ENGINO.PT";

      return {
        id: `socio-${String(item.nif ?? name)}`,
        name,
        role,
        detail,
      };
    })
    .filter((row): row is ManagersPanelRow => Boolean(row));

export const getManagersPanelRows = (report: StandReport): ManagersPanelRow[] => {
  const evidence = getManagersEvidence(report);

  if (evidence?.status === "checked") {
    const management = Array.isArray(evidence.raw?.management) ? mapManagementRows(evidence.raw.management) : [];
    const socios = Array.isArray(evidence.raw?.socios) ? mapSocioRows(evidence.raw.socios) : [];
    const rows = [...management, ...socios];

    if (rows.length > 0) {
      return rows;
    }
  }

  return report.entity.managers.map((name) => ({
    id: `manager-${name}`,
    name,
    role: "Gerente",
    detail: "Sem dados estruturados via MJ.ENGINO.PT",
  }));
};

export const getManagersPanelSourceLabel = (report: StandReport) => {
  const evidence = getManagersEvidence(report);

  if (evidence?.status === "checked" && evidence.raw?.sociosSource === "mj_engino_api") {
    return "Dados estruturados via MJ.ENGINO.PT; cruzamento judicial por nome mantém cautela por homónimos.";
  }

  if (evidence?.status === "not_configured") {
    return "MJ.ENGINO.PT ainda não está configurado neste ambiente.";
  }

  return "Quando existem gerentes conhecidos, o relatório cruza nomes com citações editais públicas.";
};
